import { Icon } from "@animech/ui";
import {
	ButtonHTMLAttributes,
	forwardRef,
	FunctionComponent,
	ReactNode,
	useEffect,
	useRef,
} from "react";
import * as Styled from "./room-panel.style";
import { RoomPanelVariant } from "./room-panel.style";
import { useUiData } from "src/ui/data";
import {
	FloorStyleName,
	floorStyleAssetName,
	floorStyleNames,
	wallColorNames,
	wallColors,
} from "src/scene/room";

export function floorThumbnailUrl(name: FloorStyleName): string {
	return `resources/images/room/${floorStyleAssetName(name)}.png`;
}

export type RoomButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
	open: boolean;
};

/** The desktop ROOM pill that opens the panel. */
export const RoomButton = forwardRef<HTMLButtonElement, RoomButtonProps>(
	({ open, ...rest }, ref) => {
		const { localizer } = useUiData();
		return (
			<Styled.Pill
				ref={ref}
				type="button"
				aria-expanded={open}
				data-open={open}
				{...rest}
			>
				<Styled.HomeIcon name="home" />
				<Styled.PillLabel>
					{localizer.localizeTerm("roomPanelTitle")}
				</Styled.PillLabel>
			</Styled.Pill>
		);
	},
);
RoomButton.displayName = "RoomButton";

export type RoomPopoverProps = {
	open: boolean;
	onClose(): void;
	/** The button the popover is anchored above. */
	children: ReactNode;
};

/**
 * Anchors the desktop panel above its button and closes it on a click
 * anywhere outside the two.
 */
export const RoomPopover: FunctionComponent<RoomPopoverProps> = ({
	open,
	onClose,
	children,
}) => {
	const anchorRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!open) {
			return;
		}
		const onPointerDown = (event: PointerEvent) => {
			if (!anchorRef.current?.contains(event.target as Node)) {
				onClose();
			}
		};
		document.addEventListener("pointerdown", onPointerDown);
		return () => document.removeEventListener("pointerdown", onPointerDown);
	}, [open, onClose]);

	return (
		<Styled.Anchor ref={anchorRef}>
			{children}
			{open && <RoomPanel variant="desktop" onClose={onClose} />}
		</Styled.Anchor>
	);
};

export type RoomPanelProps = {
	variant: RoomPanelVariant;
	onClose(): void;
};

/** Wall colour swatches and floor style tiles; a pick applies immediately. */
export const RoomPanel: FunctionComponent<RoomPanelProps> = ({
	variant,
	onClose,
}) => {
	const { room, localizer } = useUiData();
	const title = localizer.localizeTerm("roomPanelTitle");
	const wallColorLabel = localizer.localizeTerm("roomPanelWallColor");
	const floorLabel = localizer.localizeTerm("roomPanelFloor");
	const selectedWallName = localizer.localizeTerm(
		`wallColor:${room.wallColor}`,
	);
	const mobile = variant === "mobile";

	return (
		<Styled.Panel variant={variant} role="dialog" aria-label={title}>
			<Styled.Header>
				<Styled.HomeIcon name="home" />
				<Styled.Title>{title}</Styled.Title>
				<Styled.CloseButton
					type="button"
					onClick={onClose}
					aria-label={localizer.localizeTerm("close")}
				>
					<Icon name="close" />
				</Styled.CloseButton>
			</Styled.Header>
			<Styled.Body>
				<Styled.Section>
					<Styled.SectionLabelRow>
						<Styled.SectionLabel>{wallColorLabel}</Styled.SectionLabel>
						{mobile && (
							<Styled.SelectedName>{selectedWallName}</Styled.SelectedName>
						)}
					</Styled.SectionLabelRow>
					<Styled.Swatches role="radiogroup" aria-label={wallColorLabel}>
						{wallColorNames.map((name) => {
							const label = localizer.localizeTerm(`wallColor:${name}`);
							return (
								<li key={name} role="presentation">
									<Styled.Swatch
										type="button"
										role="radio"
										aria-checked={name === room.wallColor}
										aria-label={label}
										title={label}
										style={{ background: `#${wallColors[name]}` }}
										onClick={() => room.setWallColor(name)}
									/>
								</li>
							);
						})}
					</Styled.Swatches>
					{!mobile && (
						<Styled.Caption>
							<Styled.SelectedName>{selectedWallName}</Styled.SelectedName>
						</Styled.Caption>
					)}
				</Styled.Section>
				{!mobile && <Styled.Divider />}
				<Styled.Section>
					<Styled.SectionLabelRow>
						<Styled.SectionLabel>{floorLabel}</Styled.SectionLabel>
					</Styled.SectionLabelRow>
					<Styled.Tiles role="radiogroup" aria-label={floorLabel}>
						{floorStyleNames.map((name) => {
							const label = localizer.localizeTerm(`floorStyle:${name}`);
							return (
								<li key={name} role="presentation">
									<Styled.Tile
										type="button"
										role="radio"
										aria-checked={name === room.floorStyle}
										aria-label={label}
										onClick={() => room.setFloorStyle(name)}
									>
										<Styled.Thumbnail
											style={{
												backgroundImage: `url("${floorThumbnailUrl(name)}")`,
											}}
										/>
										<Styled.TileLabel>{label}</Styled.TileLabel>
									</Styled.Tile>
								</li>
							);
						})}
					</Styled.Tiles>
				</Styled.Section>
			</Styled.Body>
		</Styled.Panel>
	);
};
